import { watch, type ComputedRef, type WatchStopHandle } from 'vue'
import type { QueryClient } from '@tanstack/vue-query'

import {
  aquecerCatalogo,
  criarHashContextoCatalogo,
  type CatalogoDescriptor,
  type ContextoDeCatalogo,
} from '@next/compartilhados/catalogos'
import { DESCRITOR_CATALOGO_EMPREGADO } from '@next/modulos/estrutura-de-ponto-da-empresa/empregado/catalogo/catalogo-empregado.tipos'
import { DESCRITOR_CATALOGO_EMPREGADOR } from '@next/modulos/estrutura-de-ponto-da-empresa/empregador/catalogo/catalogo-empregador.tipos'
import { DESCRITOR_CATALOGO_LOCAL_DE_TRABALHO } from '@next/modulos/estrutura-de-ponto-da-empresa/local-de-trabalho/catalogo/catalogo-local-de-trabalho.tipos'
import { DESCRITOR_CATALOGO_MOTIVO_PADRAO } from '@next/modulos/estrutura-de-ponto-da-empresa/motivo-padrao/catalogo/catalogo-motivo-padrao.tipos'
import { DESCRITOR_CATALOGO_GRUPO_DE_LOCAIS_DE_TRABALHO } from '@next/modulos/governanca/grupo-de-locais-de-trabalho/catalogo/catalogo-grupo-de-locais-de-trabalho.tipos'
import { DESCRITOR_CATALOGO_PERFIL_DE_PERMISSAO } from '@next/modulos/governanca/perfil-de-permissao/catalogo/catalogo-perfil-de-permissao.tipos'
import { DESCRITOR_CATALOGO_REGRA_DE_CALCULO } from '@next/modulos/folha-de-ponto/regra-de-calculo/catalogo/catalogo-regra-de-calculo.tipos'
import { DESCRITOR_CATALOGO_USUARIO } from '@next/modulos/governanca/usuario/catalogo/catalogo-usuario.tipos'

import type { InfraestruturaAplicacao } from './criar-infraestrutura'
import { comporCatalogoEmpregado } from './modulos/empregado.composicao'
import { comporCatalogoEmpregador } from './modulos/empregador.composicao'
import { comporCatalogoGrupoDeLocaisDeTrabalho } from './modulos/grupo-de-locais-de-trabalho.composicao'
import { comporCatalogoLocalDeTrabalho } from './modulos/local-de-trabalho.composicao'
import { comporCatalogoMotivoPadrao } from './modulos/motivo-padrao.composicao'
import { comporCatalogoPerfilDePermissao } from './modulos/perfil-de-permissao.composicao'
import { comporCatalogoRegraDeCalculo } from './modulos/regra-de-calculo.composicao'
import { comporCatalogoUsuario } from './modulos/usuario.composicao'

export type TarefaAquecimentoCatalogo = (
  contexto: ContextoDeCatalogo,
) => Promise<void>

const criarTarefaAquecimento = <T>(
  queryClient: QueryClient,
  infraestrutura: InfraestruturaAplicacao,
  catalogo: CatalogoDescriptor<T>,
  consultar: (sinal: AbortSignal) => Promise<readonly T[]>,
  contextoAtual?: ComputedRef<ContextoDeCatalogo | null>,
): TarefaAquecimentoCatalogo => async (contexto) => {
  await aquecerCatalogo(
    queryClient,
    infraestrutura.catalogosPersistidos,
    catalogo,
    contexto,
    consultar,
    () => contextoAtual === undefined || (contextoAtual.value !== null
      && criarHashContextoCatalogo(contextoAtual.value) === criarHashContextoCatalogo(contexto)),
  )
}

export const criarTarefasAquecimentoCatalogos = (
  queryClient: QueryClient,
  infraestrutura: InfraestruturaAplicacao,
  contextoAtual?: ComputedRef<ContextoDeCatalogo | null>,
): readonly TarefaAquecimentoCatalogo[] => {
  const localDeTrabalho = comporCatalogoLocalDeTrabalho(infraestrutura)
  const empregador = comporCatalogoEmpregador(infraestrutura)
  const empregado = comporCatalogoEmpregado(infraestrutura)
  const grupo = comporCatalogoGrupoDeLocaisDeTrabalho(infraestrutura)
  const perfil = comporCatalogoPerfilDePermissao(infraestrutura)
  const usuario = comporCatalogoUsuario(infraestrutura)
  const motivo = comporCatalogoMotivoPadrao(infraestrutura)
  const regraDeCalculo = comporCatalogoRegraDeCalculo(infraestrutura)

  return Object.freeze([
    criarTarefaAquecimento(
      queryClient,
      infraestrutura,
      DESCRITOR_CATALOGO_LOCAL_DE_TRABALHO,
      localDeTrabalho.listarOpcoes,
      contextoAtual,
    ),
    criarTarefaAquecimento(
      queryClient,
      infraestrutura,
      DESCRITOR_CATALOGO_EMPREGADOR,
      empregador.listarOpcoes,
      contextoAtual,
    ),
    criarTarefaAquecimento(
      queryClient,
      infraestrutura,
      DESCRITOR_CATALOGO_EMPREGADO,
      empregado.listarOpcoes,
      contextoAtual,
    ),
    criarTarefaAquecimento(
      queryClient,
      infraestrutura,
      DESCRITOR_CATALOGO_GRUPO_DE_LOCAIS_DE_TRABALHO,
      grupo.listarOpcoes,
      contextoAtual,
    ),
    criarTarefaAquecimento(
      queryClient,
      infraestrutura,
      DESCRITOR_CATALOGO_PERFIL_DE_PERMISSAO,
      perfil.listarOpcoes,
      contextoAtual,
    ),
    criarTarefaAquecimento(
      queryClient,
      infraestrutura,
      DESCRITOR_CATALOGO_USUARIO,
      usuario.listarOpcoes,
      contextoAtual,
    ),
    criarTarefaAquecimento(
      queryClient,
      infraestrutura,
      DESCRITOR_CATALOGO_MOTIVO_PADRAO,
      motivo.listarOpcoes,
      contextoAtual,
    ),
    criarTarefaAquecimento(
      queryClient,
      infraestrutura,
      DESCRITOR_CATALOGO_REGRA_DE_CALCULO,
      regraDeCalculo.listarOpcoes,
      contextoAtual,
    ),
  ])
}

export const iniciarAquecimentoCatalogos = (
  contexto: ComputedRef<ContextoDeCatalogo | null>,
  tarefas: readonly TarefaAquecimentoCatalogo[],
): WatchStopHandle => watch(contexto, (contextoAtual) => {
  if (!contextoAtual) return

  void Promise.allSettled(
    tarefas.map(async (tarefa) => tarefa(contextoAtual)),
  )
}, { immediate: true })
